// The library namespaces every layout it persists under this prefix.
const STORAGE_KEY_PREFIX = 'react-resizable-panels';

type LegacyLayoutRecord = Record<string, { layout?: number[] }>;

/**
 * Reads a layout saved by the pre-v4 library, which the library's own
 * migration cannot restore here: it keyed each combination by panel ids sorted
 * alphabetically while storing the sizes in panel order, so zipping the key
 * against the sizes mismatches whenever the two orders differ. Taking the
 * order from the rendered panels instead pairs them correctly.
 *
 * Safe to delete once these layouts have aged out of users' browsers.
 */
export const readLegacyLayout = (
  groupId: string,
  panelIds: string[] | undefined,
): Record<string, number> | undefined => {
  if (!panelIds) return undefined;
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}:${groupId}`);
    if (!stored) return undefined;
    const record = JSON.parse(stored) as LegacyLayoutRecord;
    const sortedKey = [...panelIds]
      .sort((a, b) => a.localeCompare(b))
      .join(',');
    const layout = record[sortedKey]?.layout;
    if (!Array.isArray(layout) || layout.length !== panelIds.length) {
      return undefined;
    }
    return Object.fromEntries(panelIds.map((id, index) => [id, layout[index]]));
  } catch {
    return undefined;
  }
};
